<template>
  <MainStructure>
    <div class="content-lab-hub d-flex flex-column align-items-center w-100 pt-4 pb-5">

      <div class="title w-80 d-flex flex-column text-start">
        <h3 class="fw-bold mb-2">Laboratório Digital</h3>
        <p class="text-muted">Escolha o laboratório para exercitar as práticas industriais.</p>
      </div>

      <div v-if="labStore.loading" class="spinner-border text-primary mt-3">
        <span class="visually-hidden">Loading...</span>
      </div>

      <div v-else class="cards-grid d-flex justify-content-start flex-wrap gap-5 mt-4 w-80">
        <div
            v-for="(lab, index) in labStore.laboratories"
            :key="lab.id"
            class="lab-card d-flex flex-column align-items-center text-center p-4 bg-white rounded-4 shadow-sm"
            :data-tour="index === 0 ? 'lab-card' : undefined"
        >
          <div class="icon-container d-flex justify-content-center align-items-center mb-4 rounded-4">
            <i :class="lab.icon" class="lab-icon"></i>
          </div>

          <h5 class="fw-bold mb-3">{{ lab.title }}</h5>
          <p class="text-muted flex-grow-1 px-2" style="font-size: 0.95rem; line-height: 1.5;">
            {{ lab.description }}
          </p>

          <button
              @click="selectLaboratory(lab)"
              class="btn btn-primary custom-background-blue w-100 mt-3 rounded-3 py-2 fw-semibold"
          >
            Acessar Laboratório
          </button>
        </div>
      </div>

      <div v-if="!labStore.loading" class="teacher-tip d-flex align-items-center mt-5 w-80 p-4 rounded-4" data-tour="teacher-tip">
        <div class="tip-icon-container me-4 d-flex justify-content-center align-items-center rounded-3 custom-background-blue text-white">
          <i class="bi bi-info-circle" style="font-size: 1.8rem;"></i>
        </div>
        <div>
          <h6 class="fw-bold mb-1">Dica do Docente</h6>
          <p class="text-muted mb-0" style="font-size: 0.95rem;">
            Recomendamos seguir a ordem numérica das práticas, pois elas foram desenhadas com um grau de complexidade incremental para melhor aproveitamento do conteúdo.
          </p>
        </div>
      </div>

    </div>
  </MainStructure>
</template>

<script setup>
import { onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useLaboratoryStore } from '@/stores/laboratoryStore';
import { useAuthStore } from '@/stores/auth';
import { useTour } from '@/composables/useTour';
import ProfileEnum from '@/enums/profileEnum';
import MainStructure from '@/components/layoutParts/MainStructure.vue';

const router = useRouter();
const labStore = useLaboratoryStore();
const authStore = useAuthStore();
const { startTour, registerTourListener } = useTour();

const TOUR_NAME = 'labHub';

function getTourSteps() {
  const profile = authStore.getLoggedInUsernameAndName()?.profile;
  const steps = [
    { popover: { title: 'Boas-vindas', description: 'Bem-vindo ao Laboratório Digital! Aqui você desenvolve habilidades técnicas por meio de práticas simuladas.' } },
    { element: '[data-tour="lab-card"]', popover: { title: 'Laboratórios', description: 'Cada laboratório contém trilhas de práticas organizadas por tema.\nClique no Botão "Acessar Laboratório" para entrar no laboratório e visualizar as práticas disponíveis.' } }
  ];

  if (profile === ProfileEnum.TEACHER) {
    steps.push({ element: '[data-tour="teacher-tip"]', popover: { title: 'Dica do docente', description: 'Siga a ordem sugerida para uma melhor experiência de aprendizado.' } });
  }

  return steps;
}

registerTourListener(TOUR_NAME, getTourSteps);

onMounted(async () => {
  await labStore.fetchLaboratories();
  await nextTick();
  startTour(TOUR_NAME, getTourSteps());
});

function selectLaboratory(lab) {
  labStore.setCurrentLab(lab);
  router.push({ name: 'ListExercises', params: { lab_slug: lab.slug } });
}

</script>

<style scoped>
.w-80 {
  width: 80%;
  max-width: 1200px;
}

.lab-card {
  width: 360px;
  border: 1.5px solid #e0e5ed;
  transition: all 0.3s ease;
}

.lab-card:hover {
  transform: translateY(-5px);
  border-color: #1A5FD5;
  box-shadow: 0 10px 20px rgba(26, 95, 213, 0.1) !important;
}

.icon-container {
  width: 90px;
  height: 90px;
  background-color: #f0f5ff;
}

.lab-icon {
  font-size: 2.8rem;
  color: #1A5FD5;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.lab-card:hover .lab-icon {
  transform: scale(1.2);
}

.custom-background-blue {
  background-color: #1A5FD5;
  border-color: #1A5FD5;
}

.custom-background-blue:hover {
  background-color: #154eb0;
  border-color: #154eb0;
}

.teacher-tip {
  background-color: #ffffff;
  border: 1px solid #e0e5ed;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}

.tip-icon-container {
  width: 60px;
  height: 60px;
  min-width: 60px;
}
</style>